import { AfterViewInit, Component, HostListener, inject, ViewChild } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';
import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { ScrAuthenticationService } from './services/authentication.service';
import { Title } from '@angular/platform-browser';
import { ScrEnvironment } from 'src/environments/environment';
import { ScrServiceManager } from './services/servicemanager.service';
import { Router } from '@angular/router';
import { IuiMessagesComponent, Message } from './shared/messages/messages.component';
import { TranslateService } from '@ngx-translate/core';
import { IuiBusyComponent } from './shared/busy/busy.component';
import { JwtHelperService } from '@auth0/angular-jwt';

import { DecodedToken } from './services';
import { ExperimentalFeaturesService } from './services/experiments.service';
import { REVISION } from '../environments/revision';
import { HttpClient } from '@angular/common/http';
import { NewVersionDialogComponent } from './shared/new-version-dialog/new-version-dialog.component';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit {
  public timeout = 3600;
  public timeoutWarning = 120;

  public displaySignedOutDialog = false;

  outstandingRequests = 0;

  protected translate: TranslateService;
  private xService: ExperimentalFeaturesService;

  public logLevel = NgxLoggerLevel.DEBUG;

  @ViewChild(IuiBusyComponent, { static: false }) public busy?: IuiBusyComponent;

  @ViewChild(NewVersionDialogComponent, { static: false }) public newVersionDialog?: NewVersionDialogComponent;

  @ViewChild('globalmessage', { static: false }) globalmessage!: IuiMessagesComponent;

  constructor(
    public sm: ScrServiceManager,
    titleService: Title,
    private auth: ScrAuthenticationService,
    private messageService: MessageService,
    private httpClient: HttpClient,
    private router: Router,
    private scrServiceManager: ScrServiceManager,
    public logger: NGXLogger,
    private clipboard: Clipboard,
    private environment: ScrEnvironment) {

    // restore the saved log level if present.
    const lvl = localStorage.getItem('loglevel');
    if (lvl) {
      this.logLevel = parseInt(lvl);
    }

    this.translate = inject(TranslateService);

    this.xService = inject(ExperimentalFeaturesService);

    titleService.setTitle(environment.productName);

    this.sm.outstandingRequestCount.subscribe((count: number) => {
      this.setOutstanding(count);
    });

    this.scrServiceManager.currentMsg.subscribe(event => {
      if (event.body !== undefined) {
        const msg = new Message(event.title, event.body);
        this.displayGlobalMessage(event.type, msg, event.autoclear);
      }
      else if (event.code) {
        this.translate.get(event.code, event.args).toPromise().then(m => {
          const msg = new Message(undefined, m);
          this.displayGlobalMessage(event.type, msg, event.autoclear);
        });
      }
      else if (event.autoclear) {
        this.globalmessage.clearAll();
      }
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === 'visible') {
        // page became visible.  Check authorization.
        const url = this.router.url;
        const mustReload = this.auth.pageVisibilityChangeCheckAuth(url);
        if (mustReload) {
          this.logger.debug(`must reload is ${mustReload}.   Display modal and reload using url ${url}`);
          this.displaySignedOutDialog = true;
        }
      }
    }, false);
  }

  public reloadDueToSignOut(): void {
    window.location.reload();
  }

  private displayGlobalMessage(type = 'info', msg: Message, autoclear = false): void {
    if (autoclear) {
      this.globalmessage.clearAll();
    }
    if (type === 'success') {
      msg.autoHide = this.environment.servicedesk ? 0 : 10_000;
      this.globalmessage.displaySuccess(msg);
    } else if (type === 'info') {
      msg.autoHide = this.environment.servicedesk ? 0 : 10_000;
      this.globalmessage.displayInfo(msg);
    } else if (type === 'warning') {
      this.globalmessage.displayWarning(msg);
    }
    else if (type === 'error') {
      this.globalmessage.displayError(msg);
    }
  }

  @HostListener('document:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.defaultPrevented) {
      return;
    }
    if ((event.code === 'KeyC') && (event.altKey)) {
      this.scrServiceManager.setOutstanding(0);
    }
    else if ((event.code === 'KeyA') && (event.altKey)) {
      this.copyAccountId();
    }
    else if ((event.code === 'KeyX') && (event.altKey)) {
      this.toggleExperimentalFeatures();
    }
    else {
      if (event.altKey) {
        this.logger.trace('code: ' + event.code.toLowerCase());
      }
    }
  }

/**
 * In response to Shift-Option/Alt-X.
 */
  private toggleExperimentalFeatures(): void {
    this.xService.toggle();
    const enabled = this.xService.isEnabled();
    this.messageService.add({ severity: 'success', summary: `Features are ${enabled ? 'Enabled' : 'Disabled'}`});
  }

  /**
   * In response to Shift-Option/Alt-A, copy the account id to the clipboard.
   */
  private copyAccountId(): void {
    const state = this.auth.getAuthState();
    const accountId = state?.pendoInfo?.account?.id;
    if (accountId) {
      this.clipboard.copy(`${accountId}`);
      this.messageService.add({ severity: 'success', summary: `${accountId} copied to clipboard.` });
    }
    else {
      this.logger.warn('no accountId in auth state ' + JSON.stringify(state?.pendoInfo));
      this.auth.getIdToken().then(t => {
        if (t) {
          const s = new JwtHelperService();
          const decoded = s.decodeToken<DecodedToken>(t);
          if (decoded) {
            const accountId = decoded['custom:accountId'];
            if (accountId) {
              this.clipboard.copy(`${accountId}`);
              this.messageService.add({ severity: 'success', summary: `${accountId} copied to clipboard.` });
            }
            else {
              this.logger.warn('no accountId in token');
            }
          }
          else {
            this.logger.warn('cannot decode jwt');
          }
        }
      });
    }
  }

  setOutstanding(out: number): void {
    this.outstandingRequests = out;
    if (!this.busy) {
      this.logger.info('no busy comp');
      return;
    }
    if (out === 0) {
      this.busy.hide();
    } else {
      this.busy.show();
    }
  }

  ngAfterViewInit(): void {
    if (history.state && history.state.message) {
      history.state.message = {};
    }

    if (this.busy) {
      this.busy.hide();
    }

    this.verifyRevision();
  }

  /**
   * Compare REVISION with the revision.txt file.
   */
  verifyRevision(): void {

    // if the URL includes the ?t= parameter, clear it.
    let hadRtParam = false;
    if (window.location.search.includes('rt=')) {
      const url = window.location.pathname;
      hadRtParam = true;
      window.history.replaceState({}, '', url);
    }


    this.httpClient.get('assets/revision.txt', {
      responseType: 'text'
    }).subscribe({
      next: (rsp) => {
        const deployed = rsp.trim();
        if (deployed !== REVISION) {
          this.logger.warn(`Revision mismatch.  Deployed: ${deployed}  Environment: ${REVISION}`);
          if (this.newVersionDialog && hadRtParam === false) {
            this.newVersionDialog.displayDialog = true;
          }
        }
      }
    });

  }
}
